import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderLock, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useMeta } from "@/lib/hooks";
import { Card, EmptyState, PageHeader, SkeletonRows } from "@/components/common";
import { AttachmentItem } from "@/components/Attachments";
import { fmtBytes } from "@/lib/brand";

export default function Documents() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [owner, setOwner] = useState("");
  const [history, setHistory] = useState(false);
  const [data, setData] = useState(null);
  const { meta, entities } = useMeta();
  const load = useCallback(() => {
    api.get("/attachments", { params: { q, category_id: cat, owner_id: owner, include_history: history } }).then(({ data }) => setData(data)).catch(() => setData({ items: [], storage_used: 0, storage_quota: 1 }));
  }, [q, cat, owner, history]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="documents-page">
      <PageHeader title="Documents" subtitle="Your private vault — every file stays linked to the reminder it belongs to." actions={<Link to="/reminders/new?mode=upload" className="pm-btn pm-btn-primary" data-testid="documents-upload-btn"><Upload className="w-4 h-4" /> Upload Document</Link>} />
      <div className="grid lg:grid-cols-[1fr_280px] gap-6 items-start">
        <Card>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <input className="pm-input" placeholder="Search files or reminders" value={q} onChange={(e) => setQ(e.target.value)} data-testid="documents-search-input" />
            <select className="pm-input" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="documents-category-filter"><option value="">All categories</option>{meta?.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
            <select className="pm-input" value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="documents-owner-filter"><option value="">All people & assets</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select>
          </div>
          <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={history} onChange={(e) => setHistory(e.target.checked)} className="accent-[var(--pm-primary)]" data-testid="documents-history-toggle" /> Include previous versions from renewals</label>
          {!data ? <SkeletonRows /> : data.items.length ? data.items.map((a) => (
            <div key={a.id}><AttachmentItem a={a} showReminder onDeleted={load} /><Link to={`/reminders/${a.reminder_id}`} className="text-xs text-[var(--pm-primary)] font-semibold -mt-2 mb-2 block pl-[52px]">Open reminder →</Link></div>
          )) : <EmptyState icon={FolderLock} title="No documents yet" text="Upload a passport scan, insurance policy or receipt and link it to a reminder." action={<Link to="/reminders/new?mode=upload" className="pm-btn pm-btn-primary">Upload Document</Link>} />}
        </Card>
        {data && (
          <Card title="Storage" testid="documents-storage-card">
            <p className="text-3xl font-extrabold">{fmtBytes(data.storage_used)}</p>
            <p className="text-sm text-[var(--pm-muted)]">of {fmtBytes(data.storage_quota)} used</p>
            <div className="h-2 rounded-full bg-[#ECE9F5] mt-4 overflow-hidden"><div className="h-full" style={{ width: `${Math.max(2, (data.storage_used / data.storage_quota) * 100)}%`, background: "var(--pm-gradient)" }} /></div>
            <p className="text-xs text-[var(--pm-muted)] mt-4">Supported: PDF, JPG, PNG, WebP · max 10 MB per file. Files are private to your account and never included in exports.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
